import type {
  useFormatDate,
  useFormatting,
} from '@pflegenavi/frontend/localization';
import { ReceiptStatusValues } from '@pflegenavi/shared/api';
import type { ReceiptTableRowModel } from '../interfaces/ReceiptTableRowModel';
import { YEAR_MONTH_DAY_SHORT_FORMAT } from '@pflegenavi/shared/constants';
import type { ReceiptPhoenix } from '@pflegenavi/frontend/api-nursing-home';

interface formatReceiptTableRowModelOpts {
  fDate: ReturnType<typeof useFormatDate>;
  fCurrencyCents: ReturnType<typeof useFormatting>['fCurrency'];
  t: (key: string) => string;
}

export const formatReceiptTableRowModel = (
  transaction: ReceiptPhoenix,
  opts: formatReceiptTableRowModelOpts
): ReceiptTableRowModel => {
  const residentNameString = `${transaction.resident?.first_name} ${transaction.resident?.last_name}`;
  let employeeNameString = [
    transaction.created_by_employee?.first_name,
    transaction.created_by_employee?.last_name,
  ]
    .filter(Boolean)
    .join(' ');

  if (employeeNameString === '') {
    employeeNameString = '--';
  }

  const receiptTypeString = transaction.receipt_type
    ? opts.t(`receiptType.${transaction.receipt_type.name}`)
    : '--';
  const serviceProviderString = transaction.service_provider?.name || '--';
  const amountString = opts.fCurrencyCents(transaction.amount);
  const additionalFeeAmountString = opts.fCurrencyCents(
    transaction.additional_fee_amount
  );
  const includedFeeAmountString = opts.fCurrencyCents(
    transaction.included_fee_amount
  );
  const dateString = opts.fDate(
    transaction.receipt_date,
    YEAR_MONTH_DAY_SHORT_FORMAT
  );

  // Use settlement_date for submission date display when receipt is settled or awaiting settlement
  const receiptStatus =
    transaction.receipt_state as unknown as ReceiptStatusValues;
  const displayDate =
    (receiptStatus === ReceiptStatusValues.AWAITING_SETTLEMENT ||
      receiptStatus === ReceiptStatusValues.SETTLED) &&
    transaction.settlement_date
      ? transaction.settlement_date
      : transaction.submission_date;

  const submissionDateString = displayDate
    ? opts.fDate(displayDate, YEAR_MONTH_DAY_SHORT_FORMAT)
    : undefined;
  const statusString = opts.t(`receipts.status.${transaction.receipt_state}`);

  return {
    id: transaction.id,
    residentNameString,
    employeeNameString,
    residentFirstName: transaction.resident?.first_name || '',
    residentLastName: transaction.resident?.last_name || '',
    residentId: transaction.resident?.id || '',
    receiptTypeString,
    serviceProviderString,
    amountString,
    additionalFeeAmountString: additionalFeeAmountString,
    includedFeeAmountString: includedFeeAmountString,
    amount: transaction.amount,
    additionalFeeAmount: transaction.additional_fee_amount,
    includedFeeAmount: transaction.included_fee_amount,
    dateString,
    submissionDateString,
    date: transaction.receipt_date,
    submissionDate: displayDate || undefined,
    created_on: transaction.inserted_at,
    statusString,
    status: receiptStatus,
    receiptImageIds: transaction.receipt_image_ids,
    receipt_not_available: transaction.receipt_not_available,
    cancellation: transaction.cancellation
      ? {
          transactionId: transaction.cancellation.transaction_id,
          cancelTransactionId: transaction.cancellation.cancel_transaction_id,
          employee: {
            firstName: transaction.cancellation.employee?.first_name || '',
            lastName: transaction.cancellation.employee?.last_name || '',
          },
          note: transaction.cancellation.note,
          date: transaction.cancellation.date,
        }
      : undefined,
  };
};
